import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { forkJoin, of, Subject } from 'rxjs';
import { catchError, finalize, switchMap, takeUntil } from 'rxjs/operators';
import { ApiService } from '../../../services/api.service';
import { MessageService } from 'primeng/api';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TooltipModule } from 'primeng/tooltip';
import { environment } from '../../../../environments/environment';
import QRCode from 'qrcode';
import { ToggleButtonModule } from 'primeng/togglebutton';
import { DropdownModule } from 'primeng/dropdown';
import { MultiSelectModule } from 'primeng/multiselect';
import { ToastModule } from 'primeng/toast';
import { ActivatedRoute } from '@angular/router';
import { CommentingSeetComponent } from '../../stape-component/commenting-seet/commenting-seet.component';
import { SharedModule } from '../../../shared/shared.module';
import { RouterModule } from '@angular/router';
import { HttpClient } from '@angular/common/http';

@Component({
  selector: 'app-cfi-transaction',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    TableModule,
    ButtonModule,
    InputTextModule,
    TooltipModule,
    ToggleButtonModule,
    DropdownModule,
    MultiSelectModule,
    ToastModule,
    CommentingSeetComponent,
    SharedModule,
    RouterModule
  ],
  template: `

<div class=" transition-all duration-200 ease-in-out'max-h-[1200px] opacity-100" > 
    <div class=" py-2 px-2">
      <div class="flex items-center gap-2 overflow-x-auto pb-1 custom-scrollbar">
        <ng-container *ngFor="let item of timelineDataList; let i = index; trackBy: trackByUnitId">
          <div class="flex items-center group min-w-0">
            <div 
              class="relative w-32 flex-shrink-0 border-[1.5px] rounded-lg py-2 px-2 shadow-xs transition hover:shadow-md cursor-pointer group select-none z-10
                flex flex-col gap-1
                "
              [ngClass]="{
                'border-emerald-200 bg-emerald-50 hover:border-emerald-400': !item.is_active,
                'border-amber-300 bg-amber-50 hover:border-amber-400': item.is_active,
              }">
              
              <div class="flex items-center justify-between mb-1">
                <span class="text-[10px] font-semibold text-slate-400 flex items-center">
                  <i class="far fa-calendar-alt mr-1 text-slate-300"></i>{{ item.timestamp | date:'dd/MM' }}
                </span>
                <span class="px-1 py-0.5 rounded text-[9px] font-bold uppercase tracking-tight shadow-xs"
                      [ngClass]="{
                        'bg-emerald-100 text-emerald-800': !item.is_active,
                        'bg-amber-100 text-amber-800 animate-pulse': item.is_active,
                      }">
                  {{ item.is_active ? 'Working' : 'Pending' }}
                </span>
              </div>

              <div class="flex gap-2 items-center mb-1 truncate">
                <i class="fas fa-dot-circle text-xs mt-0.5"
                   [ngClass]="{
                     'text-emerald-400': !item.is_active,
                     'text-amber-400': item.is_active,
                   }"></i>
                <div class="truncate font-semibold text-slate-800 text-xs leading-snug">
                  {{ item?.editors[0]?.unit_name || "UNIT" }}
                </div>
              </div>

              <div 
                (click)="item?.editors?.length > 0 ? handleUnitClick(item) : null" 
                class="flex items-center justify-between pt-1 border-t border-slate-100 mt-1 cursor-pointer"
                [ngClass]="item?.editors.length > 0 ? 'hover:bg-indigo-50 rounded transition-shadow': ''"
              >
                <span class="text-[10px] text-slate-600 font-medium flex items-center">
                  <i class="fas fa-users mr-1 text-indigo-400"></i>
                  <span class="font-bold">
                    {{item.editors[0]?.is_external_user? item?.editors?.length -1 : item?.editors?.length || 0}}
                  </span>
                  <span class="ml-1 text-slate-400 font-normal">Units</span>
                </span>
                <div class="w-5 h-5 ml-1 rounded-full flex items-center justify-center transition-colors duration-150"
                  [ngClass]="item?.editors.length > 0 ? 'group-hover:bg-indigo-600 group-hover:text-white bg-slate-200 text-indigo-600' : 'bg-slate-100 text-slate-400'"
                >
                  <i class="fas fa-chevron-right text-[8px]"></i>
                </div>
              </div>
            </div>
            <div *ngIf="i < timelineDataList.length - 1" class="flex-shrink-0 w-4 flex items-center justify-center">
              <div class="h-[2px] w-full bg-gray-300"></div>
            </div>
          </div>
        </ng-container>
      </div>
    </div>
<div class="relative overflow-hidden bg-white border border-gray-200 shadow-sm rounded-xl">
  <div class="absolute top-0 left-0 w-full h-1"
    [ngClass]="step_details?.is_approved ? 'bg-emerald-500' : 'bg-amber-500'"></div>

  <div class="flex flex-col lg:flex-row lg:items-center gap-2 p-2">
    <!-- Indent Reference -->
    <div class="flex items-center gap-3 pr-4 border-r border-gray-100 min-w-[220px]">
      <div class="flex items-center justify-center w-12 h-12 rounded bg-indigo-50 text-indigo-700">
        <!-- CFI relevant icon: using a suitable clipboard-list for CFI workflow reference -->
        <i class="fas fa-clipboard-list text-3xl"></i>
      </div>
      <div>
        <p class="text-xs font-bold text-gray-400 uppercase tracking-tight mb-0.5">Indent Ref</p>
        <h4 class="text-xl font-black text-slate-800 leading-tight">{{step_details?.indent_no}}</h4>
      </div>
    </div>

    <!-- Master/Non Master Counts -->
    <div class="flex flex-col lg:flex-row gap-2 flex-1 max-w-[500px]">
      <!-- In Master Items -->
      <div class="flex-1 bg-white border border-gray-200 rounded-xl shadow-sm relative min-w-[150px]">
        <div class="absolute top-0 left-0 w-1 h-full bg-blue-500 rounded-l-xl"></div>
        <div class="p-2 pl-4 flex  justify-between">
          <div class="flex items-center gap-1 mb-1 flex-col">
            <div class="flex items-center gap-1" >
              <i class="pi pi-database text-blue-500 text-xs"></i>
              <span class="text-[9px] font-bold text-gray-400 uppercase tracking-widest">Master Items</span>
            </div>
            <div class="flex items-end justify-between">
              <div class="text-xl font-black text-slate-800 leading-none">
                {{countData?.in_master_count || 0}}
              </div>
            </div>
          </div>
          <div class="flex gap-2 bg-slate-50 px-2 py-1 rounded border border-slate-100 items-center">
            <div class="text-center mr-1">
              <p class="text-[8px] font-bold text-emerald-600 uppercase mb-0">Indigenised</p>
              <p class="text-xs font-bold text-emerald-700 leading-none m-0">{{countData?.indigenized_count || 0}}</p>
            </div>
            <div class="w-px h-4 bg-slate-200"></div>
            <div class="text-center ml-1">
              <p class="text-[8px] font-bold text-amber-600 uppercase mb-0">Non-Indig.</p>
              <p class="text-xs font-bold text-amber-700 leading-none m-0">{{countData?.not_indigenized_count.length || 0}}</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Not In Master -->
      <div class="flex-1 bg-white border border-gray-200 rounded-xl shadow-sm relative min-w-[150px]">
        <div class="absolute top-0 left-0 w-1 h-full bg-rose-500 rounded-l-xl"></div>
        <div class="p-2 pl-4 flex  justify-between">
          <div class="flex items-center gap-1 flex-col">
          <div class="flex items-center gap-1 ">
            <i class="pi pi-exclamation-circle text-rose-500 text-xs"></i>
            <span class="text-[9px] font-bold text-gray-400 uppercase tracking-widest">Not Master</span>
          </div>
          <div class="flex items-end justify-between">
            <div class="text-xl font-black text-slate-800 leading-none">
              {{countData?.not_in_master_count.length || 0}}
            </div>
          </div>
          </div>
          <div  *ngIf="showMasterButtons" class="flex items-end  flex-col justify-between">
            <a 
              [routerLink]="['/master/cfi']" 
              class="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-600 rounded border border-blue-100 hover:bg-blue-600 hover:text-white transition-all duration-150 shadow-sm text-[10px] font-bold"
              style="text-decoration: none;"
            >
              GO TO MASTER
              <i class="pi pi-external-link text-[9px]"></i>
            </a>
            <button
              (click)="pushToMaster()"
              class="group flex items-center gap-1 px-2 py-1 bg-rose-50 text-rose-600 rounded border border-rose-100 hover:bg-rose-600 hover:text-white transition-all duration-150 shadow-sm active:scale-95 text-[10px] font-bold">
              PUSH TO MASTER
              <i class="pi pi-arrow-right text-[8px] group-hover:translate-x-1 transition-transform"></i>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Approval Status & Action Btn -->
    <div class="flex items-center gap-2 ml-auto min-w-fit">
      <span class="px-2 py-1 rounded text-[10px] font-bold border"
        [ngClass]="step_details?.is_approved
          ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
          : 'bg-amber-50 text-amber-700 border-amber-200'">
        {{ step_details?.is_approved ? "APPROVED" : "PENDING" }}
      </span>
      <button
        *ngIf="step_details?.can_edit"
        (click)="showConfirm()"
        class="flex items-center gap-1 px-3 py-1.5 text-xs font-bold text-white bg-indigo-600 rounded hover:bg-indigo-700 shadow-sm active:scale-95 transition-transform">
        <i class="pi pi-save"></i>
        <span>Save/Forward</span>
      </button>
    </div>
  </div>
</div>
    <div class="fixed inset-0 z-40 flex items-center justify-center bg-black bg-opacity-50 transition-opacity"   *ngIf="displayDialog">
    <!-- Scrollable content wrapper for controlling overflow -->
    <div class="w-full h-full flex items-center justify-center overflow-auto">
      <!-- Popup Content Card -->
      <div
        class="relative max-w-2xl w-full bg-white rounded-xl shadow-2xl p-6 md:p-8 z-50 animate-fadeIn max-h-[90vh] overflow-y-auto">
        <!-- Close Button -->
        <button type="button" (click)="displayDialog = false"
          class="absolute top-3 right-3 p-2 text-slate-500 hover:text-blue-600 rounded-full hover:bg-slate-100 transition"
          aria-label="Close">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <!-- Header -->
        <div class="mb-6">
          <h1 class="text-xl md:text-3xl font-bold text-slate-800 flex items-center gap-3">
            <svg class="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            Approve & Forward
          </h1>
        </div>

        <!-- Warning Banner -->
        <div *ngIf="shareType === 'external'&& !signatureData"
          class="bg-gradient-to-r from-amber-100 to-orange-100 border-l-4 border-amber-500 p-4 rounded-r-lg my-3">
          <div class="flex items-center gap-3">
            <svg class="w-6 h-6 text-amber-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77-1.333.192 3 1.732 3z">
              </path>
            </svg>
            <p class="text-sm font-medium text-amber-800">External Routing: Digital signature required</p>
          </div>
        </div>
        <div class="mb-8">
          <label class="block text-sm font-semibold text-slate-700 mb-3">Share With:</label>
          <div class="inline-flex rounded-lg border border-slate-300 overflow-hidden shadow-sm">
            <button type="button" [disabled]="!accessObje.edit" (click)="shareType = 'internal'" [class.bg-slate-800]="shareType === 'internal'"
              [class.text-white]="shareType === 'internal'" [class.bg-white]="shareType !== 'internal'"
              [class.text-slate-700]="shareType !== 'internal'"
              class="px-6 py-2.5 font-medium text-sm transition-all duration-200 flex items-center gap-2">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
              </svg>
              Internal
            </button>
            <button type="button" [disabled]="!accessObje.edit" (click)="shareType = 'external'" [class.bg-slate-800]="shareType === 'external'"
              [class.text-white]="shareType === 'external'" [class.bg-white]="shareType !== 'external'"
              [class.text-slate-700]="shareType !== 'external'"
              class="px-6 py-2.5 font-medium text-sm transition-all duration-200 flex items-center gap-2 border-l border-slate-300">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9">
                </path>
              </svg>
              External
            </button>
          </div>
        </div>

        <div *ngIf="!accessObje.edit &&(accessObje.comment || accessObje.view)" class="bg-amber-50 border-l-4 border-amber-400 p-2 mb-2 rounded shadow-sm">
          <div class="flex items-center gap-2 mb-1">
            <div class="w-5 h-5 rounded-full bg-amber-500 flex items-center justify-center">
              <i class="fas fa-exclamation-circle text-white text-[10px]"></i>
            </div>
            <span class="text-[10px] font-bold text-amber-900 uppercase tracking-wider">
              Permission Note
            </span>
          </div>
          <div class="text-[10px] leading-relaxed font-medium text-amber-800">
            Since you have comment authority so you can only forward file Internally
          </div>
        </div>

        <!-- External Section -->
        <div *ngIf="shareType === 'external'" class="space-y-6 mb-8">
          <div class="mb-6">
            <label class="block text-sm font-semibold text-slate-700 mb-3">Permission Type:</label>
            <div class="flex flex-wrap gap-3">
              <label class="flex items-center cursor-pointer">
                <input type="radio" [disabled]="!accessObje.edit" [(ngModel)]="permissionType" [value]="'edit'" name="internal-permission"
                  class="w-4 h-4 text-blue-600 border-slate-300 focus:ring-2 focus:ring-blue-500" />
                <span class="ml-2 text-sm font-medium text-slate-700">Edit/Approve/Recommend</span>
              </label>
              <!-- <label class="flex items-center cursor-pointer">
                <input type="radio" [disabled]="!accessObje.view" [(ngModel)]="permissionType" [value]="'view'" name="internal-permission"
                  class="w-4 h-4 text-blue-600 border-slate-300 focus:ring-2 focus:ring-blue-500" />
                <span class="ml-2 text-sm font-medium text-slate-700">View</span>
              </label> -->
              <label class="flex items-center cursor-pointer">
                <input type="radio" [disabled]="!accessObje.comment" [(ngModel)]="permissionType" [value]="'comment'" name="internal-permission"
                  class="w-4 h-4 text-blue-600 border-slate-300 focus:ring-2 focus:ring-blue-500" />
                <span class="ml-2 text-sm font-medium text-slate-700">Comment</span>
              </label>
            </div>
          </div>


          <div>
            <label class="block text-sm font-semibold text-slate-700 mb-2">Select Command</label>
            <div class="w-full">
              <p-dropdown [(ngModel)]="selectedCommand" [options]="commandList" optionLabel="name" optionValue="id"
                [placeholder]="'Choose a command'" name="command" styleClass="w-full rounded" appendTo="body">
              </p-dropdown>
            </div>
          </div>
          <div>
            <label class="block text-sm font-semibold text-slate-700 mb-2">Select Unit</label>
            <p-dropdown *ngIf="permissionType === 'edit'" [(ngModel)]="selectedUnit" [options]="unitList" optionValue="id"
              optionLabel="name" filter="true" filterBy="name" [placeholder]="'Choose a unit'" name="unit"
              styleClass="w-full rounded" appendTo="body">
            </p-dropdown>
            <p-multiSelect *ngIf="permissionType !== 'edit'" [(ngModel)]="selectedUnits" [options]="unitList" optionValue="id"
              optionLabel="name" optionValue="id" filter="true" filterBy="name" [placeholder]="'Choose a unit'" name="unit"
              styleClass="w-full rounded" appendTo="body">
            </p-multiSelect>
          </div>
        </div>

        <!-- Internal Section -->
        <div *ngIf="shareType === 'internal'" class="mb-8">
          <!-- Permission Type -->
          <div class="mb-6">
            <label class="block text-sm font-semibold text-slate-700 mb-3">Permission Type:</label>
            <div class="flex flex-wrap gap-3">
              <label class="flex items-center cursor-pointer">
                <input type="radio" [disabled]="!accessObje.edit" [(ngModel)]="permissionType" [value]="'edit'" name="internal-permission"
                  class="w-4 h-4 text-blue-600 border-slate-300 focus:ring-2 focus:ring-blue-500" />
                <span class="ml-2 text-sm font-medium text-slate-700">Edit/Approve/Recommend</span>
              </label>
              <!-- <label class="flex items-center cursor-pointer">
                <input type="radio" [disabled]="!accessObje.view" [(ngModel)]="permissionType" [value]="'view'" name="internal-permission"
                  class="w-4 h-4 text-blue-600 border-slate-300 focus:ring-2 focus:ring-blue-500" />
                <span class="ml-2 text-sm font-medium text-slate-700">View</span>
              </label> -->
              <label class="flex items-center cursor-pointer">
                <input type="radio" [disabled]="!accessObje.comment" [(ngModel)]="permissionType" [value]="'comment'" name="internal-permission"
                  class="w-4 h-4 text-blue-600 border-slate-300 focus:ring-2 focus:ring-blue-500" />
                <span class="ml-2 text-sm font-medium text-slate-700">Comment</span>
              </label>
            </div>
          </div>
          <div class="mb-4">
            <label class="flex items-center cursor-pointer">
              <input [disabled]="permissionType !== 'edit'" type="checkbox" [(ngModel)]="isFinalApproval" name="final-approval"
                class="w-4 h-4 text-blue-600 border-slate-300 focus:ring-2 focus:ring-blue-500" />
              <span class="ml-2 text-sm font-medium text-slate-700">Final Approval</span>
            </label>
          </div>
          <!-- User Selection -->
          <div>
            <label class="block text-sm font-semibold text-slate-700 mb-2">
              {{ permissionType === 'edit' ? 'Select User' : 'Select Users' }}
            </label>
            <p-dropdown *ngIf="permissionType === 'edit'" id="userSelect" [options]="userList" optionLabel="first_name" filter="true"
              filterBy="first_name,last_name,hrcdf_designation,loginname,unit.name" [(ngModel)]="selectedUser"
              placeholder="Choose a user" appendTo="body" styleClass="w-full rounded">

              <ng-template pTemplate="selectedItem" let-user>
                <div *ngIf="user">
                  <small>
                    #<strong>{{ user.loginname }}</strong> |
                    <strong>{{ user.first_name }} {{ user.last_name }}</strong>
                  </small>
                </div>
                <div *ngIf="!user" class="dropdown-placeholder">
                  Choose a user
                </div>
              </ng-template>
              <!-- Dropdown list items -->
              <ng-template pTemplate="item" let-user>
                <div class="list-group-item list-group-item-action">
                  <small>
                    #<strong>{{ user.loginname }}</strong> |
                    <strong>{{ user.first_name }} {{ user.last_name }}</strong> | <strong>{{ user.hrcdf_designation
                      }}</strong>
                  </small>
                </div>
              </ng-template>
            </p-dropdown>

            <!-- Multi selection for View/Comment permissions -->
            <p-multiSelect *ngIf="permissionType !== 'edit'" [options]="userList" optionLabel="first_name" optionValue="id"
              filterBy="first_name,last_name,hrcdf_designation,loginname,unit.name" [(ngModel)]="selectedUsers"
              placeholder="Choose users" appendTo="body" styleClass="w-full rounded">

              <ng-template pTemplate="item" let-user>
                <div class="list-group-item list-group-item-action">
                  <small>
                    #<strong>{{ user.loginname }}</strong> |
                    <strong>{{ user.first_name }} {{ user.last_name }}</strong> | <strong>{{ user.hrcdf_designation
                      }}</strong>
                  </small>
                </div>
              </ng-template>
            </p-multiSelect>

          </div>
        </div>

        <!-- Comments -->
        <div class="mb-8">
          <label class="block text-sm font-semibold text-slate-700 mb-2">Comment </label>
          <textarea [(ngModel)]="internalComment" rows="4" placeholder="Add your comments here..."
            class="w-full px-4 py-3 border-2 biredr-gray-500 bg-slate-50 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-slate-700 resize-none"
            name="internalComment"></textarea>
          <div *ngIf="signatureData" class="flex flex-end mt-3">
            <div
              class="flex flex-wrap items-center justify-between bg-blue-50 p-2 border-2 border-blue-500 rounded-lg w-[350px]">
              <div class="flex items-center">
                <div class="mr-4 w-[70px]">
                  <img *ngIf="signatureData" [src]="signatureData.qr_code" alt="QR Code"
                    class="max-w-full h-auto rounded" />
                </div>
                <div>
                  <div class="text-xs text-gray-500 mb-1">
                    <span class="font-semibold">Electronically Signed by:</span><br>
                    {{ signatureData?.rankName || 'N/A' }}
                    {{ signatureData?.first_name }} {{ signatureData?.last_name }}
                  </div>
                  <div class="text-xs mb-0">
                    <span class="text-gray-700 font-semibold">Designation:</span>
                    {{ signatureData?.hrcdf_designation || 'N/A' }}
                  </div>
                  <div class="text-xs">
                    <span class="text-gray-700 font-semibold">Directorate:</span>
                    {{ signatureData?.unit?.name || 'N/A' }}
                  </div>
                </div>
              </div>

            </div>
          </div>
          <button *ngIf="!signatureData" type="button" (click)="addSignature()"
            class="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-all float-right mb-4">
            Add Signature
          </button>
        </div>



        <!-- Action Buttons -->
        <div class="flex flex-wrap gap-3 justify-end">
          <button type="button" (click)="displayDialog = false"
            class="px-4 py-1 bg-white border-2 border-slate-300 text-slate-700 rounded font-medium hover:bg-slate-50 transition-all duration-200 shadow-sm">
            Cancel
          </button>
          <button (click)="submitShare()" [disabled]="isLoading || (shareType === 'external'&& !signatureData)"
            [class.opacity-50]="isLoading" [class.cursor-not-allowed]="isLoading"
            class="px-4 py-1 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded font-medium hover:from-blue-700 hover:to-blue-800 transition-all duration-200 shadow-lg hover:shadow-xl flex items-center gap-2 disabled:from-slate-400 disabled:to-slate-500">
            <svg *ngIf="!isLoading" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
            </svg>
            <svg *ngIf="isLoading" class="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
              </path>
            </svg>
            {{ isLoading ? 'Submitting...' : 'Submit' }}
          </button>
        </div>
        <!-- </form> -->
      </div>
    </div>
  </div>
  
  <div *ngIf="showModal && editorData"  class="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 transition-all">

  <div
    class="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[92vh] overflow-hidden flex flex-col border border-slate-200">



    <div *ngIf="editorData && editorData.editors && editorData.editors.length > 0">
      <div *ngIf="!editorData.editors[0]?.is_external_user" class="px-4 pt-3 pb-2 bg-slate-100 border-b-2">
        <div class="flex justify-between items-start">
          <div class="flex items-end gap-2">
            <h2 class="text-2xl font-extrabold tracking-tight">
              <i class="fas fa-user-circle text-gray-300 mr-2 text-2xl group-hover:text-blue-400 transition-colors"></i>
              {{editorData.editors?.[0]?.unit_name }} User Hierarchy
            </h2>
            <div class="flex items-center gap-3 mb-1">
              <span
                class="bg-gradient-to-r from-indigo-500 to-purple-500 px-3 py-0.5 rounded-full text-[10px] tracking-wider font-bold text-white shadow-lg shadow-indigo-500/30">
                USER ID: {{ editorData.editors[0]?.loginname || 'N/A' }}
              </span>
              <span [ngClass]="getStatusColor(editorData.is_active)"
                class="px-3 py-0.5 text-white rounded-full text-[10px] uppercase tracking-wider font-bold shadow-lg">
                {{ editorData.is_active ? 'Working' : 'Pending' }}
              </span>
              <span
                class="bg-gradient-to-r from-emerald-500 to-teal-500 px-3 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-bold text-white shadow-lg shadow-emerald-500/30">
                Forwarded <i class="pi pi-send text-xs ml-1"></i>
              </span>
            </div>
          </div>
        </div>
      </div>

      <div *ngIf="editorData.editors[0]?.is_external_user" class="px-4 pt-3 pb-2 bg-slate-100 border-b-2">
        <div class="flex justify-between items-start">
          <div class="flex items-end gap-2">
            <h2 class="text-2xl font-extrabold tracking-tight">
              <i class="fas fa-user-circle text-gray-300 mr-2 text-2xl group-hover:text-blue-400 transition-colors"></i>
              {{editorData.editors?.[0]?.unit_name }} User Hierarchy
            </h2>
            <div class="flex items-center gap-3 mb-1">
              <span
                class="bg-gradient-to-r from-indigo-500 to-purple-500 px-3 py-0.5 rounded-full text-[10px] tracking-wider font-bold text-white shadow-lg shadow-indigo-500/30">
                USER ID: {{ editorData.editors[0]?.loginname || 'N/A' }}
              </span>
              <span [ngClass]="getStatusColor(editorData.is_active)"
                class="px-3 py-0.5 text-white rounded-full text-[10px] uppercase tracking-wider font-bold shadow-lg">
                External User
              </span>
              <span
                class="bg-gradient-to-r from-emerald-500 to-teal-500 px-3 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-bold text-white shadow-lg shadow-emerald-500/30">
                Forwarded <i class="pi pi-send text-xs ml-1"></i>
              </span>
              <button type="button" *ngIf="editorData.editors[0]?.user_id == currentUser"
                class="px-3 py-1.5  bg-indigo-100 hover:bg-indigo-200 text-indigo-700 text-[10px] rounded font-bold shadow-sm transition-all flex items-center gap-1"
                (click)="openDialog(); giveTheRouteconfigAccess(editorData.editors[0]); showModal=false;"
                style="line-height: 1.1;" title="Configure route for this step">
                <i class="fas fa-cogs text-[10px]"></i>
                Edit Route Config
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="p-8 overflow-y-auto space-y-10 custom-scrollbar">
      <div>
        <div class="flex items-center gap-2 mb-4">
          <div class="w-7 h-7 rounded bg-emerald-50 flex items-center justify-center">
            <i class="fas fa-sitemap text-emerald-600 text-xs"></i>
          </div>
          <h3 class="text-sm font-bold text-slate-700 uppercase tracking-tight">User Details</h3>
        </div>
        <div class="space-y-4">
          <ng-container *ngFor="let editor of editorData.editors; let idx = index">
            <div class="w-full bg-white border border-slate-100 rounded-xl p-3 shadow-sm transition-all group"
              *ngIf="!(editorData.editors[0]?.is_external_user && idx === 0)">
              <div class="flex items-center justify-between gap-3">
                <div class="flex items-center gap-3">
                  <div
                    class="w-9 h-9 rounded-lg bg-emerald-600 flex items-center justify-center text-white shadow-sm flex-shrink-0">
                    <i class="fas fa-user-circle text-lg"></i>
                  </div>
                  <div>
                    <h4
                      class="text-sm font-bold text-slate-800 leading-tight group-hover:text-emerald-700 transition-colors">
                      {{ editor.loginname }}
                      <button type="button" *ngIf="editor.user_id == currentUser && editor.can_edit"
                        class="px-1 py-[2px] bg-indigo-100 hover:bg-indigo-200 text-indigo-700 text-[8px] rounded-full font-bold shadow-sm transition-all flex items-center gap-1"
                        (click)="openDialog(); giveTheRouteconfigAccess(editor); showModal=false;"
                        style="line-height: 1.1;" title="Configure route for this step">
                        <i class="fas fa-cogs text-[8px]"></i>
                        Edit Route Config
                      </button>
                    </h4>
                    <div class="flex items-center gap-2 mt-0.5">
                      <span class="text-[10px] text-slate-400 font-medium">User ID: {{ editor.user_id }}</span>
                      <span class="text-[10px] text-slate-300">|</span>
                      <span class="text-[10px] text-orange-600 font-bold">
                        <i class="fas fa-lock-open mr-0.5 scale-75"></i>
                        <ng-container *ngIf="editor.can_edit">Edit&nbsp;</ng-container>
                        <ng-container *ngIf="editor.can_comment">Comment&nbsp;</ng-container>
                        <ng-container *ngIf="editor.can_view">View&nbsp;</ng-container>
                        <ng-container *ngIf="editor.is_external_user">Root</ng-container>
                        <ng-container
                          *ngIf="!editor.can_edit && !editor.can_comment && !editor.can_view && !editor.can_root">No
                          Permissions</ng-container>
                      </span>
                    </div>
                  </div>
                </div>
                <span [ngClass]="{ 'bg-emerald-400': editor.is_working, 'bg-amber-400': !editor.is_working }"
                  class="px-2.5 py-0.5 rounded-md text-[9px] font-bold uppercase tracking-wider text-white">
                  {{ editor.is_working ? 'Active' : 'Idle' }}
                </span>
              </div>
              <div class="mt-3 pl-10 space-y-2">
                <div class="flex items-center gap-2 text-[10px]">
                  <div class="flex items-center gap-1">
                    <i class="fas fa-user-shield" [class.text-slate-400]="!editor.can_edit"
                      [class.text-green-500]="editor.can_edit"></i>
                    <span [ngClass]="{'text-slate-400': !editor.can_edit, 'font-bold text-green-700': editor.can_edit}">
                      Can Edit: {{ editor.can_edit ? 'Yes' : 'No' }}
                    </span>
                  </div>
                  <div class="flex items-center gap-1">
                    <i class="fas fa-comments" [class.text-slate-400]="!editor.can_comment"
                      [class.text-amber-500]="editor.can_comment"></i>
                    <span
                      [ngClass]="{'text-slate-400': !editor.can_comment, 'font-bold text-amber-700': editor.can_comment}">
                      Can Comment: {{ editor.can_comment ? 'Yes' : 'No' }}
                    </span>
                  </div>
                  <div class="flex items-center gap-1">
                    <i class="fas fa-eye" [class.text-slate-400]="!editor.can_view"
                      [class.text-blue-500]="editor.can_view"></i>
                    <span [ngClass]="{'text-slate-400': !editor.can_view, 'font-bold text-blue-700': editor.can_view}">
                      Can View: {{ editor.can_view ? 'Yes' : 'No' }}
                    </span>
                  </div>
                  <div class="flex items-center gap-1">
                    <i class="fas fa-code-branch" [class.text-slate-400]="!editor.is_external_user"
                      [class.text-fuchsia-500]="editor.is_external_user"></i>
                    <span
                      [ngClass]="{'text-slate-400': !editor.is_external_user, 'font-bold text-fuchsia-700': editor.is_external_user}">
                      Can Root: {{ editor.is_external_user ? 'Yes' : 'No' }}
                    </span>
                  </div>
                </div>
                <div class="flex items-center gap-2 text-[10px] mt-2">
                  <span
                    [ngClass]="{'text-red-600 font-bold': editor.is_blocked, 'text-green-700 font-bold': !editor.is_blocked}">
                    <i class="fas" [ngClass]="editor.is_blocked ? 'fa-user-lock' : 'fa-unlock-alt'"></i>
                    {{ editor.is_blocked ? 'Blocked' : 'Not Blocked' }}
                  </span>
                  <span
                    [ngClass]="{'text-emerald-700': editor.is_external_user, 'text-slate-400': !editor.is_external_user}">
                    <i class="fas fa-globe"></i>
                    {{ editor.is_external_user ? 'External User' : 'Internal User' }}
                  </span>
                  <span
                    [ngClass]="{'text-yellow-700 font-bold': editor.is_working, 'text-slate-400': !editor.is_working}">
                    <i class="fas fa-briefcase"></i>
                    {{ editor.is_working ? 'Working' : 'Not Working' }}
                  </span>
                </div>
              </div>

              <div *ngIf="editor.commentors?.length" class="mt-3 pl-10 flex items-center gap-2 mb-3">
                <span class="text-[10px] text-slate-400 font-bold uppercase">Internal Commentors:</span>
                <span class="flex flex-wrap gap-2 mt-1">
                  <ng-container *ngFor="let cmt of editor.commentors">
                    <span *ngIf="!cmt.is_external_user"
                      class="flex items-center gap-1 px-2 py-1 rounded bg-indigo-50/80 border border-indigo-100 text-[10px] font-medium text-indigo-700">
                      <i class="fas fa-user-circle"></i> {{ cmt.loginname }}
                    </span>
                  </ng-container>
                </span>
              </div>

              <!-- <div *ngIf="editor.commentors?.length"
                class="border border-slate-300 rounded-lg overflow-hidden bg-white shadow-md">

                <button
                  (click)="editor.showExternalCommenters = !editor.showExternalCommenters; processGroups(editor.commentors)"
                  class="w-full flex items-center justify-between p-3 px-4 transition-all duration-200 group"
                  [ngClass]="editor.showExternalCommenters ? 'bg-indigo-800 text-white' : 'bg-white text-slate-700 hover:bg-slate-50'">
                  <div class="flex items-center gap-3">
                    <div class="p-1.5 rounded-md"
                      [ngClass]="editor.showExternalCommenters ? 'bg-indigo-600' : 'bg-indigo-50'">
                      <i class="fas fa-user-shield text-[12px]"
                        [ngClass]="editor.showExternalCommenters ? 'text-white' : 'text-indigo-600'"></i>
                    </div>
                    <div class="text-left">
                      <p class="text-[10px] font-black uppercase tracking-[0.15em] leading-none mb-1">External
                        Commenters</p>
                      <p class="text-[8px] font-bold opacity-70 uppercase">{{groupedCommentTurns?.length || ''}}
                        Verified Stakeholders</p>
                    </div>
                  </div>
                  <i class="fas fa-chevron-down text-[10px] transition-transform duration-300"
                    [class.rotate-180]="editor.showExternalCommenters"></i>
                </button>

                <div *ngIf="editor.showExternalCommenters" class="bg-slate-100 p-3 space-y-3">

                  <div *ngFor="let group of groupedCommentTurns"
                    class="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden transition-all duration-200 hover:border-indigo-300 hover:shadow-md">

                    <div (click)="group.expanded = !group.expanded"
                      class="flex items-center justify-between p-3 cursor-pointer select-none bg-white border-b border-slate-50">

                      <div class="flex items-center gap-4">
                        <div
                          class="flex flex-col items-center justify-center px-2 py-1 bg-indigo-50 border border-indigo-100 rounded text-indigo-700">
                          <span class="text-[7px] font-black uppercase leading-none">Turn</span>
                          <span class="text-[12px] font-black leading-none">#{{ group.turn_id }}</span>
                        </div>

                        <div class="flex flex-col">
                          <span class="text-[11px] font-black text-slate-800">{{ group.users[0]?.loginname }}</span>
                          <span class="text-[9px] font-bold text-indigo-500 uppercase tracking-tighter">Unit: {{
                            group.users[0]?.unit_name }}</span>
                        </div>
                      </div>

                      <div class="flex items-center gap-3">
                        <button *ngIf="group.users[0]?.unit_id == currentUnit"
                          (click)="$event.stopPropagation(); openDialog(); giveTheRouteconfigAccess(group.users[0]); showModal=false;"
                          class="flex items-center gap-2 px-3 py-1.5 bg-emerald-50 hover:bg-emerald-600 text-emerald-700 hover:text-white border border-emerald-200 rounded text-[9px] font-black uppercase transition-all shadow-sm active:scale-95">
                          <i class="fas fa-route text-[11px]"></i>
                          <span>Edit Route Config</span>
                        </button>

                        <div class="w-px h-6 bg-slate-100 mx-1"></div>

                        <i class="fas text-[10px] text-slate-300 transition-colors"
                          [ngClass]="group.expanded ? 'fa-minus-circle text-indigo-500' : 'fa-plus-circle'"></i>
                      </div>
                    </div>

                    <div *ngIf="group.expanded" class="p-3 pt-2 bg-slate-50/50 border-t border-slate-50">
                      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                        <ng-container *ngFor="let cmt of group.users ; let idx = index">

                          <div *ngIf="idx !== 0"
                            class="flex items-center justify-between p-2 bg-white border border-slate-200 rounded-md shadow-xs hover:border-indigo-200 transition-colors">

                            <div class="flex items-center gap-3">
                              <div
                                class="w-7 h-7 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center text-[10px] font-black text-slate-400">
                                {{ (cmt.loginname || 'U').charAt(0).toUpperCase() }}
                              </div>
                              <div class="flex flex-col leading-tight">
                                <span class="text-[10px] font-black text-slate-700">{{ cmt.loginname }}</span>
                                <span class="text-[8px] font-bold text-slate-400 uppercase">Sequence:
                                  {{cmt.sequence}}</span>
                              </div>
                            </div>

                            <div
                              class="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-slate-100 border border-slate-200">
                              <span class="w-1.5 h-1.5 rounded-full"
                                [ngClass]="cmt.is_working ? 'bg-yellow-400' : ' bg-emerald-500'"></span>
                              <span class="text-[7px] font-black text-slate-500 uppercase">{{cmt.is_working ? 'Pending' :
                                'Done'}}</span>
                            </div>
                          </div>
                        </ng-container>
                      </div>
                    </div>

                  </div>
                </div>
              </div> -->
            </div>
          </ng-container>
        </div>
      </div>
    </div>

    <div class="p-2 bg-slate-50 border-t border-slate-100 flex justify-end">
      <button (click)="closeModal()"
        class="px-6 py-2 bg-slate-200 hover:bg-slate-300 text-slate-700 font-bold rounded-xl transition-all">
        Close View
      </button>
    </div>
  </div>


</div>

<div class="flex items-center justify-between my-3 w-full">
    <div class="flex flex-nowrap items-center gap-1 bg-slate-300 p-1 rounded-xl border border-slate-400 shadow-sm overflow-x-auto">
      <ng-container *ngFor="let t of [
        { code: 'process', icon: 'pi-sitemap', txt: 'Process Flow', tip: 'View flow', action:'' },
        { code: 'comments', icon: 'pi-comments', txt: 'Comments', tip: 'View notes', action:'' },
       
      ]">
        <button 
          class="px-4 py-1 rounded-lg font-bold text-sm transition-all duration-300 flex items-center gap-2.5 whitespace-nowrap"
          [ngClass]="userGuide === t.code
            ? 'bg-slate-50 text-blue-700 shadow-md ring-1 ring-slate-300'
            : 'text-slate-700 hover:text-blue-700 hover:bg-slate-100/80'"
          (click)="userGuide = t.code; t.action && this[t.action]()"
          [pTooltip]="t.tip" tooltipPosition="top">
          
          <i class="pi text-base" [ngClass]="[t.icon, userGuide === t.code ? 'text-blue-700' : 'text-slate-500']"></i>
          <span>{{ t.txt }}</span>
        </button>
      </ng-container>
    </div>
  
   
  </div>

  <div *ngIf="userGuide === 'process'">
      <p-table #dt 
        [value]="cfiItems" 
        [rows]="10"
        [paginator]="cfiItems.length > 9"
        dataKey="id"
        [styleClass]="'p-datatable-sm'" 
        [responsive]="true"
      >
        <ng-template pTemplate="header">
          <tr>
        
            <ng-container *ngFor="let col of cfiItemsColumns">
              <th>
              <div class=" flex items-center gap-2" >
              <p-sortIcon [field]="col.field" [pSortableColumn]="col.field" styleClass="custom-sort-icon"></p-sortIcon>
              <span class="column-title">{{ col.header.toUpperCase() }}</span>
              <p-columnFilter class="text-gray-600" type="text" [showAddButton]="false" [field]="col.field" display="menu"/>
             
            </div>
              </th>
            </ng-container>
          </tr>
        </ng-template>
        <ng-template pTemplate="body" let-row let-selected="selected">
          <tr>
            <ng-container *ngFor="let col of cfiItemsColumns">
              <td>
                <ng-container [ngSwitch]="col.field">
                  <ng-container *ngSwitchCase="'in_master'">
                  <span class="badge" [ngClass]="{'bg-green-100 text-green-700 border border-green-300': row.in_master, 'bg-red-100 text-red-700 border border-red-300': !row.in_master}">{{ row.in_master ? 'In Master' : 'Not in Master' }}</span>                 
                  </ng-container>
                  <ng-container *ngSwitchCase="'master_status'">
                    <span 
                      class="badge"
                      [ngClass]="{
                        'bg-green-100 text-green-700 border border-green-300': row.master_status === 'Indigenised',
                        'bg-yellow-100 text-yellow-700 border border-yellow-300': row.master_status === 'working',
                        'bg-blue-100 text-blue-700 border border-blue-300': row.master_status === 'imported',
                        'bg-red-100 text-red-700 border border-red-300': row.master_status === 'Not Indigenised',
                        'bg-gray-100 text-gray-700 border border-gray-300': !row.master_status
                      }"
                    >
                      {{
                        row.master_status === 'Indigenised' ? 'Indigenised' :
                        row.master_status === 'Not Indigenised' ? 'Not Indigenised' :
                        row.master_status === 'working' ? 'Working Indigenisation' :
                        row.master_status === 'imported' ? 'Imported Granted' :
                        'N/A'
                      }}
                    </span>
                  </ng-container>
                  <ng-container *ngSwitchDefault>
                    {{ row[col.field] }}
                  </ng-container>
                </ng-container>
              </td>
            </ng-container>
          </tr>
        </ng-template>
        <ng-template pTemplate="emptymessage">
          <tr>
            <td [attr.colspan]="cfiItemsColumns.length + 1" class="text-center text-slate-400">
              No CFI items found.
            </td>
          </tr>
        </ng-template>
      </p-table>
  </div>
  <div *ngIf="userGuide === 'comments'">
  <div class="bg-slate-50 rounded-2xl shadow-sm border border-slate-300 p-6">
        <app-commenting-seet [id]="step_details.id" [stepDetails]="step_details" [url]="'/transaction/cfi/comment-timeline/?cfi='" [type]="true" [postUrl]="'/transaction/cfi-comments/'" ></app-commenting-seet>
      </div>
  </div>


  <p-toast position="center" key="confirm" [baseZIndex]="9999">
  <ng-template let-message pTemplate="message">
    <div class="flex flex-col w-full p-1">
      <div class="flex items-center gap-3 mb-4 bg-blue-50 p-3 rounded-xl border border-blue-100">
        <div class="w-10 h-10 bg-blue-600 text-white rounded-lg flex items-center justify-center shadow-lg">
          <i class="pi pi-send"></i>
        </div>
        <div>
          <h4 class="text-sm font-black text-blue-900 uppercase tracking-tighter">Action Required</h4>
          <!-- <p class="text-[10px] text-blue-700 font-medium">Step: {{ step_details?.name }}</p> -->
        </div>
      </div>

      <div class="flex flex-col gap-2">
        <button (click)="submitProposal(2)"
          class="w-full py-3 bg-blue-950 hover:bg-slate-900 text-white rounded-xl shadow-lg transition-all flex items-center justify-center gap-2 active:scale-95">
          <i class="pi pi-check-circle"></i>
          <span class="text-xs font-bold uppercase">Submit & Forward</span>
        </button>

        <div class="grid grid-cols-2 gap-2 mt-1">
          <button (click)="openDialog()"
            class="py-2.5 bg-white border border-slate-200 hover:border-blue-300 text-slate-700 rounded-xl text-[10px] font-bold uppercase transition-all flex items-center justify-center gap-2">
            <i class="pi pi-cog"></i> Route Config
          </button>
          <button (click)="submitProposal(1)"
            class="py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-[10px] font-bold uppercase transition-all flex items-center justify-center gap-2">
            <i class="pi pi-save"></i> Save Draft
          </button>
        </div>
      </div>
    </div>
  </ng-template>
</p-toast>


`
})
export class CfiTransactionComponent implements OnInit {
  @Input() step_details?: any;
  id: number;
  userGuide: string = 'process';
  // ====== Session Storage Info ======
  currentUser: any = sessionStorage.getItem('user_id');
  currentUnit: number = +sessionStorage.getItem('unit_id');

  // ====== Lists and Data ======
  commandList: any[] = [];
  userList: any[] = [];
  unitList: any[] = [];
  workflowData: any = [];
  timelineDataList: any = [];
  groupedCommentTurns: any[] = [];
  editorData: any = null;

  // ====== State variables ======
  isLoading: boolean = false;
  showModal: boolean = false;
  displayDialog: boolean = false;

  // ====== Access control and selections ======
  accessObje = { edit: true, view: true, comment: true };
  shareType='internal';
  permissionType: string | null = null;
  isFinalApproval: boolean = false;

  selectedUser: any = null;
  selectedUsers: any[] = [];
  selectedUnit: any = null;
  selectedUnits: any[] = [];
  selectedCommand: any = null;
  selectedUnitflow: any = null;

  // ====== Comments & Signature ======
  internalComment: string = '';
  externalComment: string = '';
  signatureData: any = null;
  cfiItems: any[] = [];
  countData: any = null;
  cfiItemsColumns: any[] = [
    { field: 'ser', header: 'Ser' },
    { field: 'part_no', header: 'Part No/Pattern No' },
    { field: 'in_master', header: 'In Master' },
    { field: 'master_status', header: 'Master Status' },
    { field: 'indenter_referance', header: 'Indenter referance no & date' },
    { field: 'project_class', header: 'Project/class of ship' },
    { field: 'main_equipment', header: 'Main equipment' },
    { field: 'equipment_code', header: 'Equipment code' },
    { field: 'equipment_category', header: 'Equipment category' },
    { field: 'item_desc', header: 'Item description' },
    { field: 'qty', header: 'Qty' },
    { field: 'brief_specs', header: 'Brief specs' },
    { field: 'substitute', header: 'Substitute available if any' },
    { field: 'unit_cost', header: 'Unit Cost' },
    { field: 'currency', header: 'Currency' },
    { field: 'total_cost', header: 'Total cost' },
    { field: 'last_source_of_supply', header: 'Last source of supply & year' },
    { field: 'lpp', header: 'LPP' },
    { field: 'frequency_requirement', header: 'Frequency of requirement' },
    { field: 'availability_stock', header: 'Availability of stock sample' },
  ];
  createForm = new FormGroup({
    ser: new FormControl(''),
    indenter_reference_no_and_date: new FormControl(''),
    project_class_of_ship: new FormControl(''),
    main_equipment: new FormControl(''),
    equipment_code: new FormControl(''),
    equipment_category: new FormControl(''),
    item_description: new FormControl('', [Validators.required, Validators.minLength(3)]),
    part_no_pattern_no: new FormControl(''),
    qty: new FormControl(1, [Validators.required, Validators.min(1)]),
    brief_specs: new FormControl(''),
    substitute_available_if_any: new FormControl(''),
    total_cost: new FormControl(null),
    last_source_of_supply_and_year: new FormControl(''),
    lpp: new FormControl(null, [Validators.pattern(/^\d+(\.\d+)?$/)]),
    frequency_of_requirement: new FormControl(''),
    availability_of_stock_sample: new FormControl(''),

  });
  showMasterButtons = false;

  constructor(private api: ApiService, private messageService: MessageService, private route: ActivatedRoute,private http: HttpClient) { }
  ngOnInit(): void {
    this.route.queryParams.subscribe((params: any) => {
      this.id = params.id;

      this.getCurrentCFI();
    });
     this.checkAccess();

  }

  checkAccess() {
  const unitId = sessionStorage.getItem('unit_id');
  const roleId = sessionStorage.getItem('user_role_id');

  this.showMasterButtons = (unitId === '8' && roleId === '9' || unitId === '1' && roleId === '6');
  console.log("unitId && roleId ",unitId , roleId );
  
}
  pushToMaster() {
    console.log(this.countData?.not_in_master_count);
  }


  getCammandList() {
    this.api.getAPI(`/master/commands/`).subscribe((res: any) => {
      this.commandList = res.data;
    });
  }

  getUserListForRouteConfig() {
    const unitId = Number(sessionStorage.getItem('unit_id'));
    this.api.getAPI(`/account/users/?unit_id=${unitId}`).subscribe((res: any) => {
      this.userList = res.data;
    });
  }

  getUnitForRouteConfig() {
    const currentUnitId = sessionStorage.getItem('unit_id');
    this.api.getAPI('/master/units/').subscribe((res: any) => {
      this.unitList = res.data.filter((u: any) => u.id != Number(currentUnitId));
    });
  }

  // ====== Dialog & Modal ======

  openDialog() {
    this.messageService.clear('confirm');
    this.displayDialog = true;
    this.getUserListForRouteConfig();
    this.getUnitForRouteConfig();
    this.getCammandList();
    this.resetForm();
  }

  closeModal(): void {
    this.showModal = false;
    this.selectedUnitflow = null;
  }

  // ====== Form / Data Utilities ======

  resetForm() {
    this.selectedUser = null;
    this.selectedUsers = [];
    this.selectedUnits = [];
    this.permissionType = null;
    this.isFinalApproval = false;
    this.selectedUnit = null;
    this.internalComment = '';
    this.externalComment = '';
    this.selectedCommand = null;
  }

  // ====== Workflow/Timeline Data ======

  getWorkflowLogs() {
    this.api.getAPI(`/file_mange/cfi/${this.step_details.id}/timeline`).subscribe((res: any) => {
      console.log(res);
      this.timelineDataList = res.timeline;
    });
  }

  // ====== Signature (QR CODE) ======

  addSignature() {
    this.signatureData = this.userList.find((user: any) => user.id == this.currentUser);
    const comment = this.signatureData;
    const qrText = `Electronically Signed by: ${comment.created_by?.first_name} ${comment.created_by?.last_name} | 
      Rank: ${comment.created_by?.rankName || 'N/A'} | 
      Directorate: ${comment.unit?.name || 'N/A'} | 
      Designation: ${comment.created_by?.hrcdf_designation || 'N/A'}`;

    this.generateQRCode(qrText).then(url => {
      this.signatureData.qr_code = url;
    }).catch(err => {
      console.error('QR Code generation failed', err);
    });
  }

  generateQRCode(text: string): Promise<string> {
    return QRCode.toDataURL(text);
  }

  // ====== Share/Editor/Comment Actions ======


  submitShare() {
    if (!this.step_details) return;
    if (this.shareType === 'external' && !this.signatureData) return;

    // Edit
    if (this.permissionType === 'edit') {
      if (this.shareType === 'internal') {
        if (!this.selectedUser) return;
        if (!this.selectedUnitflow.is_external_user) {
          this.assignEditorUser();
          this.displayDialog = false;
          return;
        }
        this.exUserSetInUser();
        this.displayDialog = false;
        return;
      }
      if (this.shareType === 'external') {
        if (!this.selectedUnit) return;
        this.assignEditorUser();
        this.displayDialog = false;
        return;
      }
    }

    // Comment
    if (this.permissionType === 'comment') {
      if (this.selectedUsers && this.shareType === 'internal' && !this.selectedUnitflow?.is_external_user) {
        this.displayDialog = false;
        this.addCommentTurn();
        return;
      }
      if (this.selectedUsers && this.shareType === 'internal' && this.selectedUnitflow?.is_external_user) {
        this.displayDialog = false;
        this.addCommentUser();
        return;
      }
      if (this.selectedUnits && this.shareType === 'external') {
        this.displayDialog = false;
        this.addCommentTurn();
        return;
      }
      return;
    }

    // View
    if (this.permissionType === 'view') {
      console.warn('View permission flow not implemented yet');
      return;
    }
  }

  assignEditorUser() {
    const payload: any = {
      cfi: this.step_details.id,
      is_external: this.shareType === 'external',
      is_final_editor: this.isFinalApproval,
      remarks: this.internalComment
    };
    if (this.shareType === 'external') {
      payload.unit_id = this.selectedUnit?.id;
    } else {
      payload.user = this.selectedUser?.id;
    }

    this.api.postAPI('/file_mange/cfi/editor/add/', payload).subscribe((res: any) => {
      this.messageService.add({ severity: 'success', summary: 'Success', detail: res?.status || 'Editor assigned successfully.' });
      this.getWorkflowLogs();
    });
  }

  exUserSetInUser() {
    const payload: any = {
      editor_turn: this.selectedUnitflow?.editor_id,
      user: this.selectedUser?.id,
      is_final_editor: this.isFinalApproval,
      remarks: this.internalComment
    };
    this.api.postAPI('/file_mange/cfi/editor/add-user/', payload).subscribe((res: any) => {
      this.messageService.add({ severity: 'success', summary: 'Success', detail: res?.status || 'External user assigned successfully.' });
      this.getWorkflowLogs();
    });
  }

  addCommentTurn() {
    const payload: any = {
      editor_turn: this.selectedUnitflow?.editor_id,
      is_external: this.shareType === 'external',
      remarks: this.internalComment
    };
    if (payload.is_external) {
      payload.unit_ids = this.selectedUnits;
    } else {
      payload.user_ids = this.selectedUsers;
    }

    this.api.postAPI('/file_mange/cfi/comment/turn/add/', payload).subscribe((res: any) => {
      this.messageService.add({ severity: 'success', summary: 'Success', detail: res?.status || 'Comment turn added successfully.' });
      this.getWorkflowLogs();
    });
  }

  addCommentUser() {
    const payload: any = {
      comment_turn: this.selectedUnitflow?.comment_turn_id,
      user_ids: this.selectedUsers.map((user: any) => user.id),
      remarks: this.internalComment
    };
    this.api.postAPI('/file_mange/cfi/comment/user/add/', payload).subscribe((res: any) => {
      this.messageService.add({ severity: 'success', summary: 'Success', detail: res?.status || 'Comment user added successfully.' });
      this.getWorkflowLogs();
    });
  }

  // ====== Permission & Group Logic ======

  handleUnitClick(unit: any): void {
    this.selectedUnitflow = unit;
    this.editorData = unit;
    this.showModal = true;
  }

  giveTheRouteconfigAccess(currentUnit?: any) {
    if (currentUnit) {
      this.selectedUnitflow = currentUnit;
    }
    // Reset all to true (default)
    this.accessObje = { edit: true, view: true, comment: true };
    if (currentUnit?.editor_id) {
      this.accessObje.edit = true;
      this.accessObje.comment = true;
      this.accessObje.view = true;
    }
    if (currentUnit?.can_comment) {
      this.accessObje.comment = true;
      this.accessObje.view = false;
      this.accessObje.edit = false;
      this.shareType = 'internal';
      this.permissionType = 'comment';
    }
    return;
  }

  processGroups(users: any[]) {
    const externalUsers = users.filter(u => u.is_external_user);
    const groups = externalUsers.reduce((acc, user) => {
      const turnId = user.comment_turn_id;
      if (!acc[turnId]) {
        acc[turnId] = {
          turn_id: turnId,
          expanded: false,
          users: []
        };
      }
      acc[turnId].users.push(user);
      return acc;
    }, {});
    this.groupedCommentTurns = Object.values(groups);
  }

  // ====== UI Helper Methods ======

  getStatusColor(status: string): string {
    switch (status) {
      case 'working':
        return 'bg-blue-500';
      case 'completed':
        return 'bg-green-500';
      case 'blocked':
        return 'bg-red-500';
      default:
        return 'bg-orange-500';
    }
  }

  trackByUnitId(index: number, unit: any): number {
    return unit.id;
  }
  getCurrentCFI() {
    const id = this.id;

    forkJoin([
      this.api.getAPI(`/transaction/cfi/${id}/`),
      this.api.getAPI(`/transaction/cfi/item/${id}/`),
      this.api.getAPI(`/file_mange/cfi/${id}/timeline`)
    ]).subscribe({
      next: ([cfiRes, itemsRes, timelineRes]: any[]) => {
        this.step_details = cfiRes;
        this.createForm.patchValue(cfiRes);
        cfiRes?.can_edit ? this.createForm.enable() : this.createForm.disable();

        this.cfiItems = itemsRes?.data || [];
        this.timelineDataList = timelineRes?.timeline || []
        this.countData = this.getCount(this.cfiItems);
      },
      error: () =>
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load CFI data.'
        })
    });
  }


submitForm() {
  const data = this.createForm.value;
  data['id'] = this.step_details.id;
  data['action'] = 'update';
  data['lpp'] =
    data['lpp'] !== null && data['lpp'] !== ''
      ? parseFloat(data['lpp'])
      : null;

  this.api.postAPI('/transaction/cfi/', data).subscribe((res: any) => {

    this.messageService.add({
      severity: 'success',
      summary: 'Success',
      detail: res.message || 'CFI submitted successfully'
    });


    this.getCurrentCFI();
  });
}

  getCount(data) {
    const counts = data.reduce((acc, item) => {
      if (item.in_master) {
        acc.in_master_count++;

        item.master_status === 'Indigenised'
          ? acc.indigenized_count++
          : acc.not_indigenized_count++;
      } else {
        acc.not_in_master_count.push(item);
      }
      return acc;
    }, {
      in_master_count: 0,
      not_in_master_count: [],
      indigenized_count: 0,
      not_indigenized_count: 0
    });
    return counts;
  }

  submitProposal(action: 1 | 2): void {
    if (action === 1) {
      this.submitForm();
    } else {
      this.submitIRA();
      this.submitForm();
    }
  }
  showConfirm(): void {
    this.messageService.clear('confirm');
    this.messageService.add({
      key: 'confirm',
      sticky: true,
      severity: 'info',
      summary: 'Are you sure you want to submit this proposal?'
    });
  }
   isSubmitting = false;
  submitIRA(): void {
    if (!this.step_details?.id) return;
    const payload = {
      editor_turn: this.step_details.editor_id,
    };
    this.api.postAPI(`/file_mange/cfi/editor/approve/`, payload).subscribe(res => {
      this.messageService.add({
        severity: 'success', summary: 'Success', detail: 'Proposal submitted successfully.'
      });
      this.isSubmitting = false;
    }
    );
  }

}
